import React, { useEffect, useState } from 'react';
import { getMissingInvoices, resolveMissing } from '../lib/api';
import { AlertTriangle, Check, ChevronLeft, ChevronRight } from 'lucide-react';

const STATUSES = ['unresolved', 'cancelled', 'deleted', 'not_generated', 'uploaded_later'];

export default function MissingInvoicesPage() {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    getMissingInvoices({ page, limit: 25, status: statusFilter }).then(r => {
      setData(r.data.missing_invoices || []);
      setTotal(r.data.total || 0);
      setPages(r.data.pages || 1);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, [page, statusFilter]);

  const handleResolve = async (id, status, remark) => {
    await resolveMissing(id, { status, admin_remark: remark });
    load();
  };

  return (
    <div className="space-y-4" data-testid="missing-invoices-page">
      <div>
        <h1 className="text-2xl font-bold text-boss-text">Missing Invoices</h1>
        <p className="text-boss-text-muted text-sm">{total} sequence gaps detected</p>
      </div>

      <div className="flex gap-3">
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }} className="px-3 py-2 rounded-lg border border-boss-border text-sm">
          <option value="">All Statuses</option>
          {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-boss-border overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-boss-border bg-boss-surface-2">
              <th className="text-left py-3 px-4 font-medium text-boss-text-muted">Bill No.</th>
              <th className="text-left py-3 px-4 font-medium text-boss-text-muted">Series</th>
              <th className="text-left py-3 px-4 font-medium text-boss-text-muted">Missing #</th>
              <th className="text-left py-3 px-4 font-medium text-boss-text-muted">FY</th>
              <th className="text-left py-3 px-4 font-medium text-boss-text-muted">Between</th>
              <th className="text-left py-3 px-4 font-medium text-boss-text-muted">Status</th>
              <th className="text-left py-3 px-4 font-medium text-boss-text-muted">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              [...Array(5)].map((_, i) => <tr key={i} className="border-b"><td colSpan={7} className="py-3 px-4"><div className="h-4 bg-boss-surface-3 rounded animate-pulse" /></td></tr>)
            ) : data.length === 0 ? (
              <tr><td colSpan={7} className="py-12 text-center text-boss-text-muted">
                <AlertTriangle size={24} className="mx-auto mb-2 text-green-500" />
                No missing invoices found!
              </td></tr>
            ) : data.map(m => (
              <tr key={m._id} className="border-b border-boss-border/50 hover:bg-boss-surface-2/50">
                <td className="py-2.5 px-4 font-mono text-xs">{m.bill_no_full}</td>
                <td className="py-2.5 px-4">{m.invoice_series}</td>
                <td className="py-2.5 px-4 font-mono font-bold text-boss-danger">{m.missing_invoice_number}</td>
                <td className="py-2.5 px-4">{m.financial_year}</td>
                <td className="py-2.5 px-4 text-xs text-boss-text-muted">#{m.previous_invoice_number} - #{m.next_invoice_number}</td>
                <td className="py-2.5 px-4">
                  <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                    m.status === 'unresolved' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                  }`}>{m.status}</span>
                </td>
                <td className="py-2.5 px-4">
                  {m.status === 'unresolved' && (
                    <select
                      onChange={e => { if (e.target.value) handleResolve(m._id, e.target.value, ''); e.target.value = ''; }}
                      className="text-xs px-2 py-1 border border-boss-border rounded"
                      defaultValue=""
                    >
                      <option value="" disabled>Resolve...</option>
                      <option value="cancelled">Cancelled</option>
                      <option value="deleted">Deleted</option>
                      <option value="not_generated">Not Generated</option>
                      <option value="uploaded_later">Uploaded Later</option>
                    </select>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-boss-border">
            <span className="text-sm text-boss-text-muted">Page {page} of {pages}</span>
            <div className="flex gap-2">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="p-2 rounded-lg border hover:bg-boss-surface-2 disabled:opacity-30"><ChevronLeft size={16} /></button>
              <button disabled={page >= pages} onClick={() => setPage(p => p + 1)} className="p-2 rounded-lg border hover:bg-boss-surface-2 disabled:opacity-30"><ChevronRight size={16} /></button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
